import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import tokenService from '../services/tokenService';
import useToastStore from '../store/useToastStore';
import { trackEvent } from '../utils/analyticsHelper';
import { trackMetaStandard } from '../config/metaPixel';

const PurchaseConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToastStore();

  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'loading'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const purchaseId = searchParams.get('purchaseId');

  useEffect(() => {
    const finalPurchaseId = purchaseId || localStorage.getItem('currentPurchaseId');

    if (finalPurchaseId) {
      // Clear the stored purchase ID
      localStorage.removeItem('currentPurchaseId');
      verifyPayment(finalPurchaseId);
    } else {
      setPaymentStatus('failed');
      setErrorMessage('Invalid purchase ID provided');
      setIsVerifying(false);
    }
  }, [purchaseId]);

  const verifyPayment = async (purchaseIdToVerify: string) => {
    try {
      setIsVerifying(true);
      setPaymentStatus('loading');

      const response = await tokenService.verifyPayment(purchaseIdToVerify);

      if (response.status === 'success' || response.status === 'completed') {
        setPaymentStatus('success');
        trackEvent('token_purchased', {
          purchase_id: purchaseIdToVerify,
          tokens: response.tokens || response.tokensAdded,
        });

        // Meta pixel — merchant acquisition funnel: paid purchase. eventID matches
        // the backend CAPI id (purchase_${purchaseId}); the server copy carries the
        // authoritative value/currency, and Meta merges the two on the shared id.
        trackMetaStandard('Purchase', `purchase_${purchaseIdToVerify}`);
        showToast('Payment successful! Tokens have been added to your account.', 'success');

        // Redirect to workspace after a short delay
        setTimeout(() => {
          navigate('/agent/workspace');
        }, 2000);
      } else {
        setPaymentStatus('failed');
        setErrorMessage(response.message || 'Payment verification failed');
        showToast(response.message || 'Payment verification failed', 'error');
      }

    } catch (error: any) {
      console.error('Error verifying payment:', error);
      setPaymentStatus('failed');
      const errorMsg = error?.response?.data?.message || 'Failed to verify payment status';
      setErrorMessage(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToWorkspace = () => {
    navigate('/agent/workspace');
  };

  const handleBackToPurchase = () => {
    navigate('/tokens/purchase');
  };

  const getStatusIcon = () => {
    if (paymentStatus === 'loading' || isVerifying) {
      return <RefreshCw className="w-16 h-16 text-blue-600 animate-spin" />;
    }

    switch (paymentStatus) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-600" />;
      case 'failed':
        return <XCircle className="w-16 h-16 text-red-600" />;
      default:
        return <RefreshCw className="w-16 h-16 text-blue-600 animate-spin" />;
    }
  };

  const getStatusTitle = () => {
    if (paymentStatus === 'loading' || isVerifying) return 'Verifying Payment...';

    switch (paymentStatus) {
      case 'success':
        return 'Payment Successful! 🎉';
      case 'failed':
        return 'Payment Failed';
      default:
        return 'Verifying Payment...';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Confirmation</h1>
          <div className="w-16 h-1 bg-blue-500 mx-auto"></div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Status Icon */}
          <div className="flex justify-center">
            {getStatusIcon()}
          </div>

          {/* Status Title */}
          <h2 className="text-2xl font-bold text-gray-900">
            {getStatusTitle()}
          </h2>

          {/* Status Message */}
          <p className="text-gray-600 text-lg">
            {paymentStatus === 'loading' ? 'Processing your payment...' :
              paymentStatus === 'success' ? 'Redirecting to workspace...' :
                errorMessage || 'An error occurred during payment verification'}
          </p>

          {/* Success Message */}
          {paymentStatus === 'success' && (
            <div className="bg-green-100 rounded-lg p-4">
              <div className="flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-semibold text-green-800">Payment Verified</span>
              </div>
              <p className="text-sm text-green-700">
                Your tokens have been added to your account
              </p>
            </div>
          )}

          {/* Error Message */}
          {paymentStatus === 'failed' && errorMessage && (
            <div className="bg-red-100 rounded-lg p-4">
              <div className="flex items-center justify-center mb-2">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="font-semibold text-red-800">Payment Failed</span>
              </div>
              <p className="text-sm text-red-700">
                {errorMessage}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {paymentStatus === 'failed' && (
            <div className="space-y-3">
              <button
                onClick={handleBackToPurchase}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Try Another Purchase
              </button>
              <button
                onClick={handleBackToWorkspace}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Back to Workspace
              </button>
            </div>
          )}

          {/* Purchase ID (for debugging) */}
          {purchaseId && (
            <div className="mt-8 pt-4 border-t border-gray-300">
              <p className="text-xs text-gray-500">
                Purchase ID: {purchaseId}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseConfirmationPage;
